import { useMemo, useRef, useState } from "react";
import type { Money } from "@core/index";
import { add, bankMovements, reconcile, zero } from "@core/index";
import { parseBankStatement, type ParsedBankStatement } from "@data/index";
import { useLedger } from "@ui/useLedger";
import { MoneyText } from "@ui/components";

/** Bank/cash accounts a statement can be reconciled against. */
function useBankAccounts() {
  const { accounts } = useLedger();
  return useMemo(
    () =>
      accounts
        .filter((a) => a.type === "asset" && /\b(bank|cash)\b/i.test(a.name))
        .sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );
}

export function Reconcile() {
  const { entries, base, entityId, scopeName } = useLedger();
  const bankAccounts = useBankAccounts();
  const fileInput = useRef<HTMLInputElement>(null);

  const [accountCode, setAccountCode] = useState(bankAccounts[0]?.code ?? "");
  const [dragging, setDragging] = useState(false);
  const [parsed, setParsed] = useState<ParsedBankStatement | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  // Keep the selected account valid once accounts load.
  if (accountCode === "" && bankAccounts[0]) setAccountCode(bankAccounts[0].code);

  async function handleFile(file: File) {
    const text = await file.text();
    setFileName(file.name);
    setParsed(parseBankStatement(text, base));
  }

  const movements = useMemo(
    () => bankMovements(entries, new Set([accountCode]), base, entityId),
    [entries, accountCode, base, entityId],
  );

  const recon = useMemo(
    () => (parsed ? reconcile(parsed.txns, movements, base) : null),
    [parsed, movements, base],
  );

  // Cash ledger: statement lines in date order with a running balance.
  const cashLedger = useMemo(() => {
    if (!parsed) return [];
    const sorted = [...parsed.txns].sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
    );
    let running: Money = zero(base);
    return sorted.map((t) => {
      running = add(running, t.amount);
      return { txn: t, running };
    });
  }, [parsed, base]);

  const matchByBankId = useMemo(
    () => new Map((recon?.matched ?? []).map((m) => [m.bank.id, m.book])),
    [recon],
  );

  function reset() {
    setParsed(null);
    setFileName(null);
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Bank Reconciliation</h1>
          <div className="sub">
            Upload a bank statement to build a cash ledger, then auto-match it against the bank
            movements posted in {scopeName}.
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: "var(--s4)" }}>
        <div className="card-body">
          <div className="row" style={{ gap: "var(--s4)", alignItems: "flex-end" }}>
            <div className="field" style={{ minWidth: 240 }}>
              <label htmlFor="recAccount">Bank account</label>
              <select
                id="recAccount"
                className="input"
                value={accountCode}
                onChange={(e) => setAccountCode(e.target.value)}
                disabled={bankAccounts.length === 0}
              >
                {bankAccounts.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.code} · {a.name}
                  </option>
                ))}
                {bankAccounts.length === 0 ? <option value="">No bank accounts</option> : null}
              </select>
            </div>
            {parsed ? (
              <button type="button" className="btn secondary" onClick={reset}>
                Upload another
              </button>
            ) : null}
          </div>
          {bankAccounts.length === 0 ? (
            <p className="hint mt3">
              Add an asset account with “Bank” or “Cash” in its name on the Chart of Accounts to
              reconcile against it.
            </p>
          ) : null}
        </div>
      </div>

      {!parsed ? (
        <div className="card">
          <div className="card-head">
            <h2>Upload statement</h2>
          </div>
          <div className="card-body">
            <div
              className={`dropzone${dragging ? " drag" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) void handleFile(file);
              }}
            >
              <div className="glyph" style={{ fontSize: 26 }} aria-hidden="true">
                ⇪
              </div>
              <p className="mt3">
                <strong>Drop a bank statement CSV here</strong> or choose a file
              </p>
              <p className="tiny">
                Date, description, and either an Amount column or Debit / Credit columns · UTF-8
              </p>
              <button
                type="button"
                className="btn secondary mt4"
                onClick={() => fileInput.current?.click()}
              >
                Choose file
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                className="visually-hidden"
                aria-label="Choose bank statement CSV"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFile(file);
                }}
              />
            </div>
          </div>
        </div>
      ) : !parsed.recognized ? (
        <div className="card">
          <div className="card-body">
            <div className="error-banner">
              Couldn’t find a date and amount in {fileName}. A bank statement needs a date column and
              either a signed <strong>Amount</strong> column or separate <strong>Debit</strong> /{" "}
              <strong>Credit</strong> columns.
            </div>
            <button type="button" className="btn secondary mt4" onClick={reset}>
              Try another file
            </button>
          </div>
        </div>
      ) : recon ? (
        <>
          <div className="card" style={{ marginBottom: "var(--s4)" }}>
            <div className="card-body">
              <div className="row" style={{ gap: "var(--s4)", flexWrap: "wrap" }}>
                <span className={`badge ${recon.reconciled ? "good" : "neutral"}`}>
                  <span className="dot" />{" "}
                  {recon.reconciled ? "Fully reconciled" : "Needs attention"}
                </span>
                <span className="badge good">{recon.matched.length} matched</span>
                <span className="badge neutral">{recon.unmatchedBank.length} on statement only</span>
                <span className="badge neutral">{recon.unmatchedBook.length} in books only</span>
                <span className="top-spacer" />
                <span className="muted tiny">
                  Statement net <MoneyText value={recon.statementTotal} colorSign /> · Cleared{" "}
                  <MoneyText value={recon.clearedTotal} colorSign />
                </span>
              </div>
            </div>
          </div>

          <div className="grid two">
            <div className="card">
              <div className="card-head">
                <h2>Cash ledger</h2>
                <span className="top-spacer" />
                <span className="badge neutral">{parsed.txns.length} lines</span>
              </div>
              <div className="card-body tight">
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th className="num">Amount</th>
                        <th className="num">Balance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashLedger.map(({ txn, running }) => {
                        const book = matchByBankId.get(txn.id);
                        return (
                          <tr key={txn.id}>
                            <td>{txn.date.slice(5)}</td>
                            <td>{txn.description || <span className="muted">—</span>}</td>
                            <td className="num">
                              <MoneyText value={txn.amount} colorSign />
                            </td>
                            <td className="num">
                              <MoneyText value={running} colorSign />
                            </td>
                            <td>
                              {book ? (
                                <span className="badge good" title={book.description}>
                                  {book.ref}
                                </span>
                              ) : (
                                <span className="badge warn">Unmatched</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2>Outstanding in books</h2>
                <span className="top-spacer" />
                <span className="badge neutral">{recon.unmatchedBook.length}</span>
              </div>
              <div className="card-body tight">
                {recon.unmatchedBook.length === 0 ? (
                  <p className="hint" style={{ padding: "var(--s4)" }}>
                    Every booked movement on this account appears on the statement.
                  </p>
                ) : (
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Ref</th>
                          <th>Description</th>
                          <th className="num">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recon.unmatchedBook.map((m) => (
                          <tr key={m.entryId + m.accountCode}>
                            <td>{m.date.slice(5)}</td>
                            <td className="acctcode">{m.ref}</td>
                            <td>{m.description}</td>
                            <td className="num">
                              <MoneyText value={m.amount} colorSign />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="hint mt3" style={{ padding: "0 var(--s4) var(--s4)" }}>
                  Lines flagged <em>Unmatched</em> in the cash ledger are on the bank but not yet in
                  the books — record them from the Journal. Lines here are booked but haven’t cleared
                  the bank.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
